import { Route, Navigate, createRoutesFromElements } from "react-router-dom";
import { AdminShell } from "@/components/layout/admin-shell";
import { TenantShell } from "@/components/layout/tenant-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

// Admin pages
import { AdminDashboard } from "@/pages/admin/AdminDashboard";
import { SupportTickets } from "@/pages/admin/SupportTickets";
import { Tenants } from "@/pages/admin/Tenants";
import { McpCatalogue } from "@/pages/admin/McpCatalogue";
import { SkillCatalogue } from "@/pages/admin/SkillCatalogue";
import { ModelCatalogue } from "@/pages/admin/ModelCatalogue";
import { PlatformSettings } from "@/pages/admin/PlatformSettings";
import { AzureSettings } from "@/pages/admin/AzureSettings";
import { Deployments } from "@/pages/admin/Deployments";
import { AgentHealth } from "@/pages/admin/AgentHealth";
import { VaultSettings } from "@/pages/admin/VaultSettings";
import { AgentUpdates } from "@/pages/admin/AgentUpdates";
import { Users } from "@/pages/admin/Users";

// Tenant pages
import { StartRedirect } from "@/pages/tenant/StartRedirect";
import { Agents } from "@/pages/tenant/Agents";
import { AgentDetail } from "@/pages/tenant/AgentDetail";
import { OrgChart } from "@/pages/tenant/OrgChart";
import { Issues } from "@/pages/tenant/Issues";
import { IssueDetail } from "@/pages/tenant/IssueDetail";
import { Chat } from "@/pages/tenant/Chat";
import { Workspace } from "@/pages/tenant/Workspace";
import { WorkflowCanvas } from "@/pages/tenant/WorkflowCanvas";
import { WorkflowRunsList } from "@/pages/tenant/WorkflowRunsList";
import { WorkflowRunDetail } from "@/pages/tenant/WorkflowRunDetail";
import { WorkflowInsightsDetail } from "@/pages/tenant/WorkflowInsightsDetail";
import { WorkflowEvaluation } from "@/pages/tenant/WorkflowEvaluation";
import { Goals } from "@/pages/tenant/Goals";
import { Tasks } from "@/pages/tenant/Tasks";
import { DebugSessions } from "@/pages/tenant/DebugSessions";
import { Approvals } from "@/pages/tenant/Approvals";
import { Costs } from "@/pages/tenant/Costs";
import { Activity } from "@/pages/tenant/Activity";
import { TenantSettingsShell } from "@/pages/tenant/settings/TenantSettingsShell";
import { TenantSettingsOverview } from "@/pages/tenant/settings/TenantSettingsOverview";
import { TenantAuthSettings } from "@/pages/tenant/settings/TenantAuthSettings";
import { TenantVaultSettings } from "@/pages/tenant/settings/TenantVaultSettings";
import { TenantMcpList } from "@/pages/tenant/settings/TenantMcpList";
import { TenantMcpDetail } from "@/pages/tenant/settings/TenantMcpDetail";
import { TenantSkillsSettings } from "@/pages/tenant/settings/TenantSkillsSettings";
import { TenantUsage } from "@/pages/tenant/settings/TenantUsage";
import { NewAgent } from "@/pages/tenant/NewAgent";
import { NewSubagent } from "@/pages/tenant/NewSubagent";
import { Hiring } from "@/pages/tenant/Hiring";
import { Notifications } from "@/pages/tenant/Notifications";
import { Artifacts } from "@/pages/tenant/Artifacts";
import { M365Settings } from "@/pages/tenant/M365Settings";
import { GoalDetail } from "@/pages/tenant/GoalDetail";
import { Inbox } from "@/pages/tenant/Inbox";
import { AgentComms } from "@/pages/tenant/AgentComms";
import { UserManagement } from "@/pages/tenant/UserManagement";
import { GroupManagement } from "@/pages/tenant/GroupManagement";
import { CustomerOnboarding } from "@/pages/tenant/CustomerOnboarding";
import { Apps } from "@/pages/tenant/Apps";
import { AppDetail } from "@/pages/tenant/AppDetail";
import { AppBuilder } from "@/pages/tenant/AppBuilder";

import { NewTenant } from "@/pages/admin/NewTenant";
import { TenantAdminModels } from "@/pages/admin/TenantAdminModels";
import { TenantAdminUsage } from "@/pages/admin/TenantAdminUsage";

// Auth
import { Auth } from "@/pages/Auth";
import { AcceptInvite } from "@/pages/AcceptInvite";
import { M365Callback } from "@/pages/M365Callback";
import { Account } from "@/pages/Account";

/**
 * The whole route tree, as data rather than as a rendered `<Routes>`.
 *
 * main.tsx feeds this to `createBrowserRouter`. That is what makes
 * `useBlocker` available, which the workflow canvas uses to stop a navigation
 * while there are unsaved changes. `<BrowserRouter>` cannot do that: its
 * blocker hook throws without a data router, so every exit had to be patched
 * by hand and the ones that call `navigate()` directly (the workspace top bar,
 * the sidebar chat list) were always missed.
 *
 * The JSX is unchanged. `createRoutesFromElements` reads the same `<Route>`
 * elements the old `<Routes>` wrapper did.
 */
export const appRoutes = createRoutesFromElements(
  <Route>
      {/* MSP Admin routes */}
      <Route element={<ProtectedRoute><AdminShell /></ProtectedRoute>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<AdminDashboard />} />
        <Route path="/support-tickets" element={<SupportTickets />} />
        <Route path="/tenants" element={<Tenants />} />
        <Route path="/tenants/new" element={<NewTenant />} />
        <Route path="/mcp-catalogue" element={<McpCatalogue />} />
        <Route path="/skill-catalogue" element={<SkillCatalogue />} />
        <Route path="/models" element={<ModelCatalogue />} />
        <Route path="/azure" element={<AzureSettings />} />
        <Route path="/deployments" element={<Deployments />} />
        <Route path="/agent-health" element={<AgentHealth />} />
        <Route path="/agent-updates" element={<AgentUpdates />} />
        <Route path="/vault" element={<VaultSettings />} />
        <Route path="/users" element={<Users />} />
        <Route path="/settings" element={<PlatformSettings />} />
        {/* MSP admin view of a single tenant — replaces the old inline
            TenantModelSettings modal with a dedicated route. */}
        <Route path="/tenants/:tenantId/admin/models" element={<TenantAdminModels />} />
        <Route path="/tenants/:tenantId/admin/usage" element={<TenantAdminUsage />} />
      </Route>

      {/* Tenant workspace routes */}
      <Route path="/tenants/:tenantId" element={<ProtectedRoute><TenantShell /></ProtectedRoute>}>
        {/* One start screen: a new chat. /dashboard still works and opens it. */}
        <Route index element={<Navigate to="workspace?newChat=1" replace />} />
        <Route path="dashboard" element={<StartRedirect />} />
        <Route path="agents" element={<Agents />} />
        <Route path="agents/new" element={<NewAgent />} />
        <Route path="agents/:agentId" element={<AgentDetail />} />
        <Route path="agents/:agentId/subagents/new" element={<NewSubagent />} />
        <Route path="org" element={<OrgChart />} />
        <Route path="issues" element={<Issues />} />
        <Route path="issues/:issueId" element={<IssueDetail />} />
        <Route path="workspace" element={<Workspace />} />
        <Route path="workflows/:workflowId" element={<WorkflowCanvas />} />
        <Route path="workflows/:workflowId/runs" element={<WorkflowRunsList />} />
        <Route path="workflows/:workflowId/insights" element={<WorkflowInsightsDetail />} />
        <Route
          path="workflows/:workflowId/evaluation"
          element={<WorkflowEvaluation />}
        />
        <Route
          path="workflows/:workflowId/runs/:runId"
          element={<WorkflowRunDetail />}
        />
        <Route
          path="workflows/:workflowId/runs/:runId/evaluation"
          element={<WorkflowEvaluation />}
        />
        <Route path="chat" element={<Navigate to="../workspace" replace />} />
        <Route path="goals" element={<Goals />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="debug/sessions" element={<DebugSessions />} />
        <Route path="goals/:goalId" element={<GoalDetail />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="costs" element={<Costs />} />
        <Route path="activity" element={<Activity />} />
        <Route path="hiring" element={<Hiring />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="artifacts" element={<Artifacts />} />
        <Route path="agent-comms" element={<AgentComms />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="groups" element={<GroupManagement />} />
        <Route path="m365" element={<M365Settings />} />
        <Route path="apps" element={<Apps />} />
        <Route path="apps/:appId" element={<AppDetail />} />
        <Route path="apps/:appId/builder" element={<AppBuilder />} />
        <Route path="settings" element={<TenantSettingsShell />}>
          <Route index element={<TenantSettingsOverview />} />
          <Route path="usage" element={<TenantUsage />} />
          <Route path="auth" element={<TenantAuthSettings />} />
          <Route path="vault" element={<TenantVaultSettings />} />
          <Route path="mcp" element={<TenantMcpList />} />
          <Route path="mcp/:mcpId" element={<TenantMcpDetail />} />
          <Route path="skills" element={<TenantSkillsSettings />} />
        </Route>
      </Route>

      {/* Customer onboarding (outside TenantShell) */}
      <Route path="/tenants/:tenantId/onboarding" element={
        <ProtectedRoute><CustomerOnboarding /></ProtectedRoute>
      } />

      {/* Account — its own shell-free page so MSP admins + tenant users share it */}
      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />

      {/* Auth */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/invite" element={<AcceptInvite />} />

      {/* M365 OAuth callback landing page */}
      <Route path="/m365-callback" element={<M365Callback />} />
  </Route>,
);
